import { Box, TablePagination, Typography } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import { ReactComponent as Star } from "../assets/kid_star.svg";
import "./DataTable.scss";
import { useCallback, useMemo, useState } from "react";
import { ReactComponent as ArrowDown } from "../assets/arrow_drop_down.svg";
import { ReactComponent as ArrowUp } from "../assets/arrow_drop_up.svg";
import { ReactComponent as Checked } from "../assets/checkbox.svg";
import { Square } from "lucide-react";
import TableSkeleton from "./TableSkeleton/TableSkeleton";

/*interface HeaderColumn {
  id: string;
  name: string;
}

interface CustomTableProps {
  rows: any[],
  headers: HeaderColumn[],
  isLoading?: boolean,
  tableType: string,
  onRowClick?: (row:any) => void,
  successCaseClick?: () => void,
}*/

export default function DataTable({
  rows,
  headers,
  isLoading = false,
  tableType,
  onRowClick,
  successCaseClick,
  onSelectionChange,
  showCheckbox = true,
}: any) {
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const handleCheckboxChange = useCallback(
    (row: any) => {
      let updatedSelection;
      if (selectedRows.some((r) => r.id === row.id)) {
        updatedSelection = selectedRows.filter((r) => r.id !== row.id);
      } else {
        updatedSelection = [...selectedRows, row];
      }

      setSelectedRows(updatedSelection);
      if (typeof onSelectionChange === "function") {
        onSelectionChange(updatedSelection);
      }
    },
    [selectedRows]
  );

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  const sortedRows = useMemo(() => {
    if (!sortConfig) return rows;

    const sorted = [...rows].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      return sortConfig.direction === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return sorted;
  }, [rows, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const formatDate = (value: string | Date) => {
    const date = new Date(value);
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };

    return new Intl.DateTimeFormat("es-MX", options).format(date);
  };

  const columnHeaders =
    headers ||
    (rows.length > 0
      ? Object.keys(rows[0]).filter((key) => key !== "id" && key !== "actions")
      : []);

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedRows.slice(start, end);
  }, [page, sortedRows, rowsPerPage]);

  const renderHeaders = (headers: any[]) => {
    const renderSortableHeader = (header: any, index: number) => {
      const isSorted = sortConfig?.key === header.id;
      const nextDirection = isSorted ? sortConfig?.direction : "asc";
      const sortIcon =
        nextDirection === "asc" ? (
          <ArrowDown
            className="sort-icon"
            style={{ opacity: isSorted ? 1 : 0.4 }}
          />
        ) : (
          <ArrowUp
            className="sort-icon"
            style={{ opacity: isSorted ? 1 : 0.4 }}
          />
        );

      return (
        <TableCell
          key={index}
          sx={{ cursor: "pointer" }}
          onClick={() => handleSort(header.id)}
        >
          <Box display="flex" alignItems="center">
            {header.name}
            {sortIcon}
          </Box>
        </TableCell>
      );
    };

    switch (tableType) {
      case "checkbox":
        return (
          <>
            <TableCell className="checkbox-col"></TableCell>
            {headers.map(renderSortableHeader)}
            <TableCell className="actions-col"></TableCell>
          </>
        );
      case "history":
        return <>{headers.map(renderSortableHeader)}</>;
      case "ideas":
        return (
          <>
            <TableCell />
            {headers.map(renderSortableHeader)}
          </>
        );
    }
  };

  const renderRow = (row: any) => {
    switch (tableType) {
      case "checkbox":
        return (
          <>
            <TableCell className="checkbox-col">
              {showCheckbox && (
                <Checkbox
                  icon={<Square />}
                  checkedIcon={<Checked />}
                  checked={selectedRows.some((r) => r.id === row.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleCheckboxChange(row);
                  }}
                  className="checkbox-col__icon"
                  disableRipple
                />
              )}
            </TableCell>
            {columnHeaders.map((key: any, index: any) => {
              return (
                <TableCell
                  key={index}
                  onClick={
                    tableType !== "history"
                      ? () => handleRowClick(row)
                      : undefined
                  }
                >
                  {row[key.id]}
                </TableCell>
              );
            })}
            <TableCell className="actions-col">
              {(row.actions || []).map((action: any) => action)}
            </TableCell>
          </>
        );
      case "history":
        return (
          <>
            {columnHeaders.map((key: any, index: any) => {
              const value = row[key.id];
              const isDate =
                value &&
                (typeof value === "string" || value instanceof Date) &&
                !isNaN(new Date(value).getTime());
              return (
                <TableCell key={index}>
                  {isDate ? formatDate(value) : value}
                </TableCell>
              );
            })}
          </>
        );
      case "ideas":
        return (
          <>
            <TableCell className="checkbox-col">
              <Star
                className={
                  !row.is_success_case ? "star-icon" : "star-icon--succes-case"
                }
                onClick={() => successCaseClick(row)}
              />
            </TableCell>
            {columnHeaders.map((key: any, index: any) => {
              return (
                <TableCell
                  key={index}
                  onClick={
                    tableType !== "history"
                      ? () => handleRowClick(row)
                      : undefined
                  }
                >
                  {row[key.id]}
                </TableCell>
              );
            })}
          </>
        );
    }
  };

  const handleRowClick = (row: any) => {
    if (tableType === "ideas") {
      onRowClick(row);
    } else if (tableType === "checkbox") {
      handleCheckboxChange(row);
    }
  };

  return (
    <Box className="table-container">
      <TableContainer component={Paper} className="table">
        <Table stickyHeader className="custom-table">
          <TableHead>
            <TableRow>{renderHeaders(headers)}</TableRow>
            <TableRow>
              <TableCell
                colSpan={headers.length + 2}
                sx={{ padding: "0 !important" }}
              >
                <Box
                  sx={{
                    height: "1px",
                    width: "95%",
                    margin: "0 auto",
                    backgroundColor: "rgba(224, 224, 224, 1)",
                  }}
                />
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow
                sx={{
                  ":hover": {
                    backgroundColor: "white !important",
                  },
                }}
              >
                <TableCell colSpan={headers.length + 2} className="table__cell">
                  <TableSkeleton mode={tableType} />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {!rows || rows.length === 0 ? (
                  <TableRow
                    sx={{
                      ":hover": {
                        backgroundColor: "white !important",
                      },
                    }}
                  >
                    <TableCell
                      colSpan={headers.length + 2}
                      sx={{
                        padding: "30px !important",
                        paddingBottom: "60px !important",
                        paddingTop: "60px !important",
                      }}
                    >
                      <Box className="no-info-cell">
                        No hay registros disponibles
                        <Typography>
                          Cuando haya información, aparecerá aquí para que la
                          consultes fácilmente.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row: any, index: any) => (
                    <>
                      <TableRow
                        className="table-row"
                        sx={{
                          cursor:
                            tableType !== "history" ? "pointer" : "default",
                        }}
                        key={index}
                      >
                        {renderRow(row)}
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={headers.length + 2}
                          sx={{ padding: "0 !important" }}
                        >
                          <Box
                            sx={{
                              height: "1px",
                              width: "95%",
                              margin: "0 auto",
                              backgroundColor: "rgba(224, 224, 224, 1)",
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    </>
                  ))
                )}
              </>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}
